import { useId, useRef, useState } from 'react';
import { RiCameraLine, RiImageAddLine, RiLoader2Fill, RiUpload2Line, RiUploadLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { Button } from './button';
import { renderIcon, type IconLike } from './icon';
import { DashedOutline } from './lib/dashed-outline';

export type FileRejectionReason = 'type' | 'size' | 'count';
export interface FileRejection {
  file: File;
  reason: FileRejectionReason;
}

export interface FileDropzoneProps {
  /** Accepted files (after `accept` / `maxSize` / `multiple` checks). */
  onFiles: (files: File[]) => void;
  onReject?: (rejections: FileRejection[]) => void;
  /** Same syntax as `<input accept>`: `"image/png,image/jpeg"`, `".svg,.png"`, `"image/*"`. */
  accept?: string;
  /** Max size per file in bytes. */
  maxSize?: number;
  multiple?: boolean;
  /**
   * `area` — dashed upload area (401:8569): White/3, 1px dashed White/30 [4,4], r8, icon tile 32 + title + hint.
   * `avatar` — 64 dashed circle + Secondary "Rasm yuklash" button + hint (501:11584).
   * `button` — full-width dashed button h42 (359:5854 "Rasm yuklash").
   */
  variant?: 'area' | 'avatar' | 'button';
  title?: React.ReactNode;
  /** e.g. "JPG yoki PNG, 2 MB gacha". */
  hint?: React.ReactNode;
  icon?: IconLike;
  /** Upload in progress: spinner, `uploadingLabel` and an optional progress bar. */
  uploading?: boolean;
  /** 0–100; shows a progress bar while `uploading`. */
  progress?: number;
  uploadingLabel?: React.ReactNode;
  /** Avatar variant: current image. */
  previewUrl?: string | null;
  /** Avatar variant: button text. Default "Rasm yuklash". */
  buttonLabel?: React.ReactNode;
  error?: React.ReactNode;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

function matchesAccept(file: File, accept: string | undefined) {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
    .some((token) => {
      if (token.startsWith('.')) return name.endsWith(token);
      if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
      return type === token;
    });
}

/** Human file size for hints: 2 MB, 512 KB. */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.round((bytes / 1024 / 1024) * 10) / 10} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** File picker + drop target (idle / drag-over / uploading). Click, Enter or Space opens the system dialog. */
export function FileDropzone({
  onFiles,
  onReject,
  accept,
  maxSize,
  multiple = false,
  variant = 'area',
  title,
  hint,
  icon,
  uploading = false,
  progress,
  uploadingLabel = 'Yuklanmoqda...',
  previewUrl,
  buttonLabel = 'Rasm yuklash',
  error,
  disabled = false,
  'aria-label': ariaLabel,
  className,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const hintId = useId();
  const inactive = disabled || uploading;

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const accepted: File[] = [];
    const rejected: FileRejection[] = [];
    Array.from(list).forEach((file, i) => {
      if (!multiple && i > 0) rejected.push({ file, reason: 'count' });
      else if (!matchesAccept(file, accept)) rejected.push({ file, reason: 'type' });
      else if (maxSize != null && file.size > maxSize) rejected.push({ file, reason: 'size' });
      else accepted.push(file);
    });
    if (accepted.length) onFiles(accepted);
    if (rejected.length) onReject?.(rejected);
  };

  const open = () => {
    if (!inactive) inputRef.current?.click();
  };

  const dropHandlers = {
    onDragEnter: (e: React.DragEvent) => {
      if (inactive || !e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
    },
    onDragOver: (e: React.DragEvent) => {
      if (inactive) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      if (!inactive) handleFiles(e.dataTransfer.files);
    },
  };

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      hidden
      accept={accept}
      multiple={multiple}
      disabled={inactive}
      onChange={(e) => {
        handleFiles(e.currentTarget.files);
        e.currentTarget.value = '';
      }}
    />
  );

  const progressBar =
    uploading && progress != null ? (
      <span className="flex w-full max-w-200 items-center gap-8">
        <progress
          max={100}
          value={Math.min(100, Math.max(0, progress))}
          className={cn(
            'h-4 flex-1 appearance-none overflow-hidden rounded-2 bg-field',
            '[&::-webkit-progress-bar]:bg-field [&::-webkit-progress-value]:rounded-2 [&::-webkit-progress-value]:bg-accent-2',
            '[&::-webkit-progress-value]:transition-[width] [&::-moz-progress-bar]:bg-accent-2',
          )}
        />
        <span className="w-32 text-right text-11 text-fg-secondary tabular-nums">{Math.round(progress)}%</span>
      </span>
    ) : null;

  const errorText = error ? (
    <span role="alert" className="text-11 text-tone-red-lighter">
      {error}
    </span>
  ) : null;

  if (variant === 'avatar') {
    return (
      <div className={cn('flex items-center gap-12', className)} {...dropHandlers}>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel ?? (typeof buttonLabel === 'string' ? buttonLabel : 'Rasm yuklash')}
          aria-busy={uploading || undefined}
          aria-describedby={hint ? hintId : undefined}
          onClick={open}
          className={cn(
            'relative flex size-64 shrink-0 items-center justify-center overflow-hidden rounded-full bg-field outline-none',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-2',
            inactive ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-line-strong',
            dragging && 'bg-blue-100',
          )}
        >
          {previewUrl ? (
            <img src={previewUrl} alt="" className="size-full object-cover" />
          ) : uploading ? (
            <RiLoader2Fill aria-hidden className="size-20 animate-spin text-icon" />
          ) : (
            renderIcon(icon ?? RiCameraLine, 'size-20 text-icon')
          )}
          {!previewUrl && <DashedOutline radius="full" className={dragging ? 'text-accent-2' : 'text-icon'} />}
        </button>
        <div className="flex min-w-0 flex-col gap-6">
          <Button variant="secondary" size="md" leftIcon={RiUpload2Line} onClick={open} disabled={disabled} loading={uploading} className="self-start">
            {uploading ? uploadingLabel : buttonLabel}
          </Button>
          {hint && (
            <span id={hintId} className="text-11 text-fg-secondary">
              {hint}
            </span>
          )}
          {progressBar}
          {errorText}
        </div>
        {fileInput}
      </div>
    );
  }

  if (variant === 'button') {
    return (
      <div className={cn('flex flex-col gap-6', className)}>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-busy={uploading || undefined}
          onClick={open}
          {...dropHandlers}
          className={cn(
            'relative flex h-42 w-full items-center justify-center gap-8 rounded-8 px-16 text-[0.84375rem] leading-auto font-medium text-fg outline-none',
            'transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-2',
            inactive ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-hover',
            dragging && 'bg-blue-100',
          )}
        >
          <DashedOutline radius={8} className={dragging ? 'text-accent-2' : 'text-white-300 light:text-black-300'} />
          {uploading ? (
            <RiLoader2Fill aria-hidden className="size-16 animate-spin text-icon" />
          ) : (
            renderIcon(icon ?? RiUploadLine, 'size-16 text-fg')
          )}
          <span>{uploading ? uploadingLabel : (title ?? buttonLabel)}</span>
        </button>
        {progressBar}
        {errorText}
        {fileInput}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
        aria-describedby={hint ? hintId : undefined}
        aria-busy={uploading || undefined}
        onClick={open}
        {...dropHandlers}
        data-dragging={dragging || undefined}
        className={cn(
          // padding 18/12 + the 1 px stroke, which Figma counts in the layout (401:8569 is 110 high, not 108)
          'relative flex w-full flex-col items-center justify-center gap-6 rounded-8 bg-white-30 px-13 py-19 text-center outline-none light:bg-black-30',
          'transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-2',
          inactive ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-hover',
          disabled && 'opacity-60',
          dragging && 'bg-blue-100 hover:bg-blue-100',
        )}
      >
        <DashedOutline radius={8} className={dragging ? 'text-accent-2' : 'text-white-300 light:text-black-300'} />
        <span className="flex size-32 items-center justify-center rounded-8 bg-field">
          {uploading ? (
            <RiLoader2Fill aria-hidden className="size-18 animate-spin text-icon" />
          ) : (
            renderIcon(icon ?? RiImageAddLine, cn('size-18', dragging ? 'text-accent-2' : 'text-icon'))
          )}
        </span>
        <span className="text-xs leading-auto font-semibold tracking-normal text-fg">
          {uploading ? uploadingLabel : (title ?? 'Fayl yuklang')}
        </span>
        {hint && !uploading && (
          <span id={hintId} className="text-11 leading-auto text-fg-secondary">
            {hint}
          </span>
        )}
        {progressBar}
      </button>
      {errorText}
      {fileInput}
    </div>
  );
}
